import { useEffect, useState } from 'react';
import { 
    IconButton, Box, Grid, Card, CardContent, Typography, 
    Button, CircularProgress, Paper, Divider, Tooltip,
    Breadcrumbs, Link, Chip
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteUser } from '../../../redux/userRelated/userHandle';
import { getAllSclasses } from '../../../redux/sclassRelated/sclassHandle';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ShowClasses = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sclassesList, loading, error, getresponse } = useSelector((state) => state.sclass);
  const { currentUser } = useSelector(state => state.user);

  const adminID = currentUser._id;

  useEffect(() => {
    dispatch(getAllSclasses(adminID, "Sclass"));
  }, [adminID, dispatch]);

  const { showToast } = useToast();

  const deleteHandler = (deleteID, address) => {
    dispatch({ type: 'sclass/getFailedTwo', payload: null });
    
    dispatch(deleteUser(deleteID, address))
      .then(() => {
        dispatch({ type: 'sclass/getSuccess', payload: [] });
        dispatch(getAllSclasses(adminID, "Sclass"));
        showToast("Class deleted successfully", "success");
      });
  };
 

  return (
    <PageContainer>
      <MainContent>
        {/* Header Section */}
        <HeaderSection>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate("/Admin/dashboard")} sx={{ bgcolor: 'white', boxShadow: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
                School Classes 🏫
              </Typography>
              <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" href="/Admin/dashboard">Dashboard</Link>
                <Typography color="text.primary">Classes</Typography>
              </Breadcrumbs>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddCircleIcon />}
            onClick={() => navigate("/Admin/addclass")}
            sx={{ 
              borderRadius: '12px',
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              }
            }}
          >
            Add New Class
          </Button>
        </HeaderSection>

        {/* Stats Row */}
        <StatsRow>
          <StatCard>
            <StatIconWrapper $color="#667eea">
              <GroupIcon />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{sclassesList?.length || 0}</Typography>
              <Typography variant="body2" color="textSecondary">Total Classes</Typography>
            </Box>
          </StatCard>
        </StatsRow>

        <Divider sx={{ mb: 4 }} />

        {loading ? (
          <LoadingWrapper>
            <CircularProgress size={60} thickness={4} />
            <Typography sx={{ mt: 2, fontWeight: 500 }}>Loading classes...</Typography>
          </LoadingWrapper>
        ) : (
          <>
            {(!sclassesList || sclassesList.length === 0 || getresponse) ? (
              <EmptyState>
                <EmptyIconWrapper>🏫</EmptyIconWrapper>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>No Classes Found</Typography>
                <Typography color="textSecondary" sx={{ mb: 3 }}>
                  Start by adding your first class to the system.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate("/Admin/addclass")}
                  sx={{ borderRadius: '10px' }}
                >
                  Create Class
                </Button>
              </EmptyState>
            ) : (
              <Grid container spacing={3}>
                {sclassesList.map((cls, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={cls._id}>
                    <StyledClassCard $delay={index * 0.1}>
                      <CardHeader $color={index % 2 === 0 ? '#667eea' : '#764ba2'}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                          Class {cls.sclassName}
                        </Typography>
                        <Box>
                           <Tooltip title="View Details">
                            <ActionIconButton onClick={() => navigate("/Admin/classes/class/" + cls._id)}>
                              <VisibilityIcon fontSize="small" />
                            </ActionIconButton>
                          </Tooltip>
                        </Box>
                      </CardHeader>
                      <CardContent sx={{ pt: 3 }}>
                        <QuickActions>
                           <ActionItem onClick={() => navigate("/Admin/addsubject/" + cls._id)}>
                              <SubjectIcon color="primary" fontSize="small" />
                              <Typography variant="caption">Add Subject</Typography>
                           </ActionItem>
                           <ActionItem onClick={() => navigate("/Admin/class/addstudents/" + cls._id)}>
                              <PersonAddIcon color="success" fontSize="small" />
                              <Typography variant="caption">Add Student</Typography>
                           </ActionItem>
                        </QuickActions>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <CardFooter>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit Class">
                              <IconButton size="small" onClick={() => navigate("/Admin/editclass/" + cls._id)}>
                                <EditIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Class">
                              <IconButton size="small" onClick={() => deleteHandler(cls._id, "Sclass")}>
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Button 
                            endIcon={<ArrowForwardIcon />} 
                            size="small"
                            onClick={() => navigate("/Admin/classes/class/" + cls._id)}
                            sx={{ fontWeight: 600 }}
                          >
                            Details
                          </Button>
                        </CardFooter>
                      </CardContent>
                    </StyledClassCard>
                  </Grid>
                ))}
              </Grid>
            )}
           </>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default ShowClasses;

// Styled Components
const PageContainer = styled(Box)`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 24px;
`;

const MainContent = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeaderSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const StatsRow = styled(Box)`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const StatCard = styled(Paper)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  background: white;
`;

const StatIconWrapper = styled(Box)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
`;

const EmptyState = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  border-radius: 20px;
  text-align: center;
  background: white;
  border: 1px dashed #ccc;
`;

const EmptyIconWrapper = styled(Box)`
  font-size: 64px;
  margin-bottom: 16px;
`;

const StyledClassCard = styled(Card)`
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08) !important;
  transition: all 0.3s ease !important;
  height: 100%;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  opacity: 0;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
  }
`;

const CardHeader = styled(Box)`
  padding: 20px;
  background: ${props => props.$color};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionIconButton = styled(IconButton)`
  background: rgba(255,255,255,0.2) !important;
  color: white !important;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: rgba(255,255,255,0.3) !important;
  }
`;

const QuickActions = styled(Box)`
  display: flex;
  justify-content: space-around;
  margin-bottom: 16px;
`;

const ActionItem = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
  
  span {
    font-weight: 600;
    color: #666;
  }
`;

const CardFooter = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;